import { Card, CardContent, CardHeader, CardTitle, cn } from "@beweegbaas/ui";
import { useAtomSet, useAtomValue } from "@effect/atom-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { BLEED } from "#/modules/les.planning/components/lesuren-week";
import type { Klas } from "#/modules/les.planning/components/rooster-cel";
import { RoosterTabel } from "#/modules/les.planning/components/rooster-tabel";
import { EMPTY_LESROOSTER } from "#/modules/les.planning/domain/lesrooster";
import {
  applyRegelEdits,
  getGymPerKlas,
  type Roosterregel,
} from "#/modules/les.planning/domain/rooster";
import {
  planningKlassenQueryOptions,
  roosterQueryOptions,
} from "#/modules/les.planning/query.planning";
import {
  flushLesroosterAtom,
  lesroosterDraftAtom,
  newRegelsAtom,
  regelEditsAtom,
} from "#/modules/les.planning/rooster/rooster-editor";
import type { TenantQueryKey } from "#/modules/util.fetch-client/tenant-query-key";

/** The school's rooster, a weekdag at a time, with how often each klas has gym. */
export function RoosterEditor({
  tenant,
  weekdag,
  onWeekdagChange,
}: {
  tenant: TenantQueryKey;
  weekdag: number;
  onWeekdagChange: (weekdag: number) => void;
}) {
  const roosterQuery = useQuery(roosterQueryOptions(tenant));
  const klassenQuery = useQuery(planningKlassenQueryOptions(tenant));
  const draft = useAtomValue(lesroosterDraftAtom);
  const edits = useAtomValue(regelEditsAtom);
  const nieuw = useAtomValue(newRegelsAtom);
  const flush = useAtomSet(flushLesroosterAtom);
  // The page's registry goes soon after the page does: a time still being typed is saved first.
  useEffect(() => () => flush(), [flush]);
  // While a time is typed in, the draft shows; once it is let go the query cache holds exactly that.
  const lesrooster = draft ?? roosterQuery.data?.lesrooster ?? EMPTY_LESROOSTER;
  const regels = applyRegelEdits(roosterQuery.data?.regels ?? [], edits, Object.values(nieuw));
  const klassen = [...(klassenQuery.data ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, "nl", { numeric: true }),
  );

  return (
    <div className="flex flex-col gap-6">
      {/* The table's rows run to the page's edges and on under the card, which floats over the
          room they keep free for it. */}
      <div className="relative flex flex-col gap-6 [--rooster-rand:var(--container-gutter)] md:[--rooster-vrij:calc(15rem+--spacing(6))]">
        <RoosterTabel
          lesrooster={lesrooster}
          weekdag={weekdag}
          regels={regels}
          klassen={klassen}
          docenten={roosterQuery.data?.docenten ?? []}
          onWeekdagChange={onWeekdagChange}
        />
        <div className="md:absolute md:top-1 md:right-0 md:z-30 md:w-60">
          <GymPerWeek regels={regels} klassen={klassen} />
        </div>
      </div>
      <p className={cn(BLEED, "text-muted-foreground m-0 border-t pt-3 text-xs")}>
        {m["planning.rooster.direct_opgeslagen"]()} {m["planning.rooster.lessen_blijven"]()}
      </p>
    </div>
  );
}

function GymPerWeek({
  regels,
  klassen,
}: {
  regels: readonly Roosterregel[];
  klassen: readonly Klas[];
}) {
  const perKlas = new Map(getGymPerKlas(regels).map((klas) => [klas.id, klas.aantal]));
  if (klassen.length === 0) return null;
  return (
    <Card size="sm" className="shadow-md" data-testid="gym-per-week">
      <CardHeader>
        <CardTitle>{m["planning.rooster.gym_per_week"]()}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-sm">
          {klassen.map((klas) => {
            const aantal = perKlas.get(klas.id) ?? 0;
            return (
              <li
                key={klas.id}
                className="flex items-baseline justify-between gap-3"
                data-klas={klas.name}
              >
                <span className="font-semibold">{klas.name}</span>
                <span className="text-muted-foreground tabular-nums">
                  {aantal === 0
                    ? m["planning.rooster.geen_gym"]()
                    : m["planning.rooster.keer_per_week"]({ count: aantal })}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
